/**
 * Mobile Sidebar Component
 * Drawer overlay for mobile views
 */

import { useTranslation } from '@spcc/core'
import { isRtlLanguage } from '@spcc/portal-shared'
import { useSidebar } from '@/providers'
import { cn, X } from '@spcc/uikit'
import { SIDEBAR_DIMENSIONS } from '../constants'
import { SidebarContent } from './sidebar-content'

// ============================================
// Helper Functions
// ============================================

function getOverlayStyles(isOpen: boolean): string {
  return cn(
    'fixed inset-0 bg-black/50 z-40 transition-opacity duration-300',
    isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
  )
}

function getDrawerStyles(isOpen: boolean, isRTL: boolean): string {
  const positionStyle = isRTL ? 'right-0' : 'left-0'
  const closedTransform = isRTL ? 'translate-x-full' : '-translate-x-full'
  const transformStyle = isOpen ? 'translate-x-0' : closedTransform

  return cn(
    'fixed top-0 bottom-0 bg-card z-50 shadow-2xl transition-transform duration-300 ease-out',
    SIDEBAR_DIMENSIONS.MOBILE_WIDTH,
    positionStyle,
    transformStyle
  )
}

function getCloseButtonStyles(isRTL: boolean): string {
  return cn(
    'absolute top-4 w-8 h-8 rounded-full bg-secondary flex items-center justify-center hover:bg-secondary/80 transition-colors z-10',
    isRTL ? 'left-4' : 'right-4'
  )
}

// ============================================
// Component
// ============================================

export function MobileSidebar() {
  const { mobileOpen, closeMobile, isMobile } = useSidebar()
  const { i18n } = useTranslation()
  const isRTL = isRtlLanguage(i18n.language)

  if (!isMobile) return null

  return (
    <>
      {/* Overlay */}
      <div
        className={getOverlayStyles(mobileOpen)}
        onClick={closeMobile}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside className={getDrawerStyles(mobileOpen, isRTL)}>
        <button
          onClick={closeMobile}
          className={getCloseButtonStyles(isRTL)}
          aria-label="Close menu"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>

        <SidebarContent onNavigate={closeMobile} />
      </aside>
    </>
  )
}
